/**
 * The DATEV tab's logic: what each state is called, what colour it gets, and when a resend is
 * offered at all (work order item 8).
 *
 * JSX-free so the dashboard's node-only Jest can reach it. The copy lives in the `opsDatev`
 * namespace; the keys that are BUILT here at runtime (one per enum member) are invisible to
 * `locale-parity.spec.ts`'s source scan, so `ops-datev.spec.ts` checks every one of them against
 * both catalogues instead.
 *
 * **Enum tokens.** State badges show translated copy, with the raw API token as the badge's hover
 * title — the pattern `support-enum-labels.ts` set for the support desk. A non-technical operator
 * reads "Confirmed by the Kanzlei's finalise"; whoever cross-checks the database still gets
 * `CONFIRMED_BY_ACCOUNTANT_FINALISE`.
 */

import {
  OpsDatevInviteStatus,
  OpsDatevLockState,
  OpsDatevPruefpunktSeverity,
  OpsRecipientConfirmationState,
  type OpsDatevInviteDto,
} from '../../../../libs/api-client/src/generated/models';
import type { Tone } from '../components/Pill';

// ── Invite status ───────────────────────────────────────────────────────────

const INVITE_STATUS_TONE: Record<OpsDatevInviteStatus, Tone> = {
  [OpsDatevInviteStatus.Pending]: 'sky',
  [OpsDatevInviteStatus.Finalizing]: 'amber',
  [OpsDatevInviteStatus.Finalized]: 'green',
  // Historical only — nothing writes it any more — so it reads as "over", not as a problem.
  [OpsDatevInviteStatus.Expired]: 'gray',
  [OpsDatevInviteStatus.Withdrawn]: 'gray',
  [OpsDatevInviteStatus.Superseded]: 'gray',
};

/** Badge tone of an invite status. */
export function inviteStatusTone(status: OpsDatevInviteStatus): Tone {
  return INVITE_STATUS_TONE[status];
}

/** Catalogue key of an invite status. */
export function inviteStatusLabelKey(status: OpsDatevInviteStatus): string {
  return `opsDatev.inviteStatus.${status}`;
}

// ── The merchant's settings lock ────────────────────────────────────────────

const LOCK_STATE_TONE: Record<OpsDatevLockState, Tone> = {
  [OpsDatevLockState.None]: 'gray',
  // Locked, and the invitation never got out: the one lock state that needs doing something.
  [OpsDatevLockState.AwaitingDelivery]: 'red',
  [OpsDatevLockState.Pending]: 'sky',
  [OpsDatevLockState.Finalized]: 'green',
  [OpsDatevLockState.Expired]: 'gray',
  [OpsDatevLockState.Withdrawn]: 'gray',
  [OpsDatevLockState.Superseded]: 'gray',
};

/** Badge tone of the merchant's DATEV settings lock. */
export function lockStateTone(state: OpsDatevLockState): Tone {
  return LOCK_STATE_TONE[state];
}

/** Catalogue key of a lock state. */
export function lockStateLabelKey(state: OpsDatevLockState): string {
  return `opsDatev.lockState.${state}`;
}

// ── Recipient confirmation (AC-8.2, AC-8.5) ─────────────────────────────────

const RECIPIENT_STATE_TONE: Record<OpsRecipientConfirmationState, Tone> = {
  [OpsRecipientConfirmationState.ConfirmedDoubleOptIn]: 'green',
  // Receives exports, but nobody at that address ever clicked a confirmation. Not an error —
  // an explicit product decision — yet exactly what AC-8.5 says must not look like a green tick.
  [OpsRecipientConfirmationState.ConfirmedByAccountantFinalise]: 'teal',
  [OpsRecipientConfirmationState.ConfirmedOriginUnknown]: 'amber',
  [OpsRecipientConfirmationState.PendingNotSent]: 'red',
  [OpsRecipientConfirmationState.PendingAwaitingConfirmation]: 'sky',
  [OpsRecipientConfirmationState.PendingLinkExpired]: 'amber',
  [OpsRecipientConfirmationState.Expired]: 'amber',
  [OpsRecipientConfirmationState.Rejected]: 'gray',
};

/** Badge tone of a recipient's confirmation state. */
export function recipientStateTone(state: OpsRecipientConfirmationState): Tone {
  return RECIPIENT_STATE_TONE[state];
}

/** Catalogue key of a recipient's confirmation state. */
export function recipientStateLabelKey(
  state: OpsRecipientConfirmationState,
): string {
  return `opsDatev.recipientState.${state}`;
}

/**
 * The one-line explanation printed under a recipient's badge — what the state MEANS for support.
 *
 * Every state has one, because the whole point of AC-8.5 is that "confirmed" alone says too
 * little: a finalise-created recipient had no second opt-in, and an unknown-origin one cannot be
 * told apart from either.
 */
export function recipientStateHintKey(
  state: OpsRecipientConfirmationState,
): string {
  return `opsDatev.recipientHint.${state}`;
}

/** Whether a recipient's state means exports are actually mailed to them. */
export function recipientReceivesExports(
  state: OpsRecipientConfirmationState,
): boolean {
  return (
    state === OpsRecipientConfirmationState.ConfirmedDoubleOptIn ||
    state === OpsRecipientConfirmationState.ConfirmedByAccountantFinalise ||
    state === OpsRecipientConfirmationState.ConfirmedOriginUnknown
  );
}

// ── Exports ─────────────────────────────────────────────────────────────────

/** Badge tone of an export job's status. The column is a string, so unknown falls to neutral. */
export function exportStatusTone(status: string): Tone {
  switch (status) {
    case 'completed':
      return 'green';
    case 'failed':
      return 'red';
    case 'processing':
      return 'sky';
    case 'pending':
      return 'amber';
    default:
      return 'gray';
  }
}

/** Badge tone of a Prüfliste code: something to act on, or something to know. */
export function pruefpunktTone(severity: OpsDatevPruefpunktSeverity): Tone {
  return severity === OpsDatevPruefpunktSeverity.Action ? 'amber' : 'gray';
}

// ── When a resend is offered (AC-8.1, AC-8.4) ───────────────────────────────

/** Whether the invite resend button is shown, and — when it is not — why, for the row's hint. */
export type InviteResendOffer =
  | { readonly offered: true }
  | { readonly offered: false; readonly whyKey: string | null };

/**
 * Whether support is offered a resend for this invite.
 *
 * The API's `resendable` is the authority (newest, PENDING, link not revoked) and this never
 * widens it. What it adds is the hint for the NEWEST row when it is not resendable, because that
 * is the row an operator is looking at when the merchant says "my Steuerberater has no email":
 * the answer there is a merchant decision, and the tab should say which. Older rows get no hint —
 * they are history.
 */
export function inviteResendOffer(
  invite: Pick<
    OpsDatevInviteDto,
    'resendable' | 'isNewest' | 'status' | 'tokenRevokedAt'
  >,
): InviteResendOffer {
  if (invite.resendable) return { offered: true };
  if (!invite.isNewest) return { offered: false, whyKey: null };
  if (invite.status === OpsDatevInviteStatus.Finalizing) {
    return { offered: false, whyKey: 'opsDatev.invites.whyNot.finalizing' };
  }
  if (invite.status === OpsDatevInviteStatus.Pending && invite.tokenRevokedAt) {
    return { offered: false, whyKey: 'opsDatev.invites.whyNot.revoked' };
  }
  return { offered: false, whyKey: 'opsDatev.invites.whyNot.merchantDecision' };
}

/**
 * The warnings the invite-resend confirm step shows, in order.
 *
 * Always: the Kanzlei's current link stops working — a resend mints a NEW token, even when the
 * send then fails. LOUDER when the Kanzlei had already opened that link: the link is single-use, so
 * they are already in (their portal session and the code login are untouched by a resend), and a
 * resend is most likely not what they need — support should be sure before rotating it.
 *
 * @param linkWasUsed - The invite's `tokenAcceptedAt` is set.
 */
export function inviteResendWarnings(linkWasUsed: boolean): {
  readonly tone: 'warning' | 'error';
  readonly keys: readonly string[];
} {
  return linkWasUsed
    ? {
        tone: 'error',
        keys: [
          'opsDatev.confirm.invite.linkStops',
          'opsDatev.confirm.invite.linkWasUsed',
        ],
      }
    : { tone: 'warning', keys: ['opsDatev.confirm.invite.linkStops'] };
}

// ── Errors ──────────────────────────────────────────────────────────────────

/**
 * Whether a failed request was the per-shop resend budget (429).
 *
 * Structural rather than `axios.isAxiosError`, so the spec can hand in a plain object and this
 * module stays free of the HTTP client.
 */
export function isRateLimited(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const record: Record<string, unknown> = { ...error };
  const response = record['response'];
  if (typeof response !== 'object' || response === null) return false;
  const status: Record<string, unknown> = { ...response };
  return status['status'] === 429;
}

// ── Every runtime-built key, for the spec ───────────────────────────────────

/** Every catalogue key this module builds at runtime. `ops-datev.spec.ts` checks both locales. */
export function opsDatevRuntimeKeys(): string[] {
  return [
    ...Object.values(OpsDatevInviteStatus).map(inviteStatusLabelKey),
    ...Object.values(OpsDatevLockState).map(lockStateLabelKey),
    ...Object.values(OpsRecipientConfirmationState).flatMap((state) => [
      recipientStateLabelKey(state),
      recipientStateHintKey(state),
    ]),
    'opsDatev.invites.whyNot.finalizing',
    'opsDatev.invites.whyNot.revoked',
    'opsDatev.invites.whyNot.merchantDecision',
    'opsDatev.confirm.invite.linkStops',
    'opsDatev.confirm.invite.linkWasUsed',
  ];
}
